import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../utils/api';

// Query keys
const USER_SUBSCRIPTIONS_QUERY_KEY = 'user-subscriptions';

// Custom hook for fetching user subscriptions with filters
export const useUserSubscriptions = (page = 1, limit = 10, filters = {}) => {
  return useQuery({
    queryKey: [USER_SUBSCRIPTIONS_QUERY_KEY, page, limit, filters],
    queryFn: async () => {
      const params = {
        page,
        limit,
        ...filters
      };
      
      // Remove undefined values
      Object.keys(params).forEach(key => {
        if (params[key] === undefined || params[key] === '') {
          delete params[key];
        }
      });

      const response = await apiClient.get('user-subscriptions', params);
      return {
        data: response.data || [],
        total: response.total || 0,
        page: response.page || 1,
        limit: response.limit || 10,
        hasNext: response.has_next || false,
        hasPrev: response.has_prev || false
      };
    },
    keepPreviousData: true,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

// Custom hook for getting a single user subscription
export const useUserSubscription = (id) => {
  return useQuery({
    queryKey: [USER_SUBSCRIPTIONS_QUERY_KEY, id],
    queryFn: () => apiClient.get(`user-subscriptions/${id}`),
    enabled: !!id,
  });
};

// Custom hook for updating user subscription status
export const useUpdateUserSubscriptionStatus = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, isActive }) => {
      return apiClient.put(`user-subscriptions/${id}/status`, { is_active: isActive });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [USER_SUBSCRIPTIONS_QUERY_KEY] });
    },
  });
};

// Custom hook for deleting a user subscription
export const useDeleteUserSubscription = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id) => apiClient.delete(`user-subscriptions/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [USER_SUBSCRIPTIONS_QUERY_KEY] });
    },
  });
};
